import React, { useEffect, useRef, useState } from "react";
import { api, wsUrl } from "./api";
import { setServerOffsetFromISO } from "./clock";

export function useGameSocket(code) {
  const [state, setState] = useState(null);
  const [connected, setConnected] = useState(false);
  const ref = useRef(null);
  const retryRef = useRef(0);
  const lastMsgAtRef = useRef(Date.now());
  const stateRef = useRef(null);

  const applyState = (msg) => {
    if (!msg || msg.type !== "state") return;
    lastMsgAtRef.current = Date.now();
    if (msg.server_time) setServerOffsetFromISO(msg.server_time);
    // Skip re-render if payload is unchanged (polling comes in often)
    const stamp = `${msg.status}|${msg.current_question?.id || ""}|${msg.stage_deadline || ""}|${msg.player_count}|${msg.top5?.map(p=>`${p.name}:${p.score}`).join(",") || ""}`;
    if (stateRef.current === stamp) return;
    stateRef.current = stamp;
    setState(msg);
  };

  useEffect(() => {
    if (!code) return;
    let stopped = false;
    const connect = () => {
      const ws = new WebSocket(wsUrl(code));
      ref.current = ws;
      let pingTimer = null;
      ws.onopen = () => {
        setConnected(true); retryRef.current = 0;
        lastMsgAtRef.current = Date.now();
        pingTimer = setInterval(() => {
          try { ws.readyState === 1 && ws.send(JSON.stringify({ type: "ping" })); } catch {}
        }, 20000);
      };
      ws.onmessage = (ev) => {
        try {
          const msg = JSON.parse(ev.data);
          applyState(msg);
        } catch {}
      };
      ws.onclose = () => {
        setConnected(false);
        if (pingTimer) { clearInterval(pingTimer); pingTimer = null; }
        if (stopped) return;
        const wait = Math.min(5000, 500 * ++retryRef.current);
        setTimeout(connect, wait);
      };
      ws.onerror = () => { try { ws.close(); } catch {} };
    };
    connect();

    // HTTP polling safety net — kicks in when WS message stops flowing (dead proxy on mobile).
    // Always ensures teacher/student view has fresh authoritative state within ~2.5s.
    const pollTimer = setInterval(async () => {
      // Only poll when tab is visible; also always poll if we haven't heard from WS recently.
      if (document.visibilityState !== "visible") return;
      const stale = Date.now() - lastMsgAtRef.current > 2500;
      if (!stale && ref.current?.readyState === 1) return;
      try {
        const r = await api.get(`/games/${code}/state`);
        applyState(r.data);
      } catch {}
    }, 2500);

    const onVis = () => {
      if (document.visibilityState === "visible") {
        const ws = ref.current;
        if (!ws || ws.readyState === 3 || ws.readyState === 2) {
          try { ws?.close(); } catch {}
          connect();
        }
        // Also immediately fetch fresh state without waiting for poll tick
        (async () => {
          try { const r = await api.get(`/games/${code}/state`); applyState(r.data); } catch {}
        })();
      }
    };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      stopped = true;
      clearInterval(pollTimer);
      document.removeEventListener("visibilitychange", onVis);
      try { ref.current?.close(); } catch {}
    };
  }, [code]);
  return { state, connected };
}
